import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ABAFile,
  PaginationParams 
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch, 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const ABA_FILES_KEY = 'aba-files';

// ==================== ABA Files ====================

export function useABAFiles(params?: PaginationParams) {
  return usePaginatedFetch<{ data: ABAFile[]; meta: { total: number } }>(
    ABA_FILES_KEY,
    '/payroll/aba',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {}
  );
}

export function useABAFile(id: string) {
  return useFetch<ABAFile>(
    [ABA_FILES_KEY, id],
    `/payroll/aba/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useGenerateABA() {
  const queryClient = useQueryClient();
  
  return useMutation<ABAFile, Error, { 
    payRunId: string; 
    description?: string;
    debitAccountBsb: string;
    debitAccountNumber: string;
    debitAccountName: string;
    userAbbreviation: string;
    userId: string;
  }>({
    mutationFn: (data) => apiClient.post('/payroll/aba/generate', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ABA_FILES_KEY] });
    },
  });
}

export function useValidateABA() {
  return useMutation<{ valid: boolean; errors: string[] }, Error, string>({
    mutationFn: (id) => apiClient.post(`/payroll/aba/${id}/validate`, {}),
  });
}

export function useDownloadABA() {
  return useMutation<Blob, Error, string>({
    mutationFn: (id) => apiClient.get(`/payroll/aba/${id}/download`, { responseType: 'blob' }),
  });
}
